import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { AccountMappingGridComponent } from '../../components/account-mapping-grid/account-mapping-grid.component';
import { Dialog } from '@angular/cdk/dialog';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { AccountMappingImportDialogComponent } from '../../components/account-mapping-import-dialog/account-mapping-import-dialog.component';
import {
  AccountMappingDialogData,
  AccountMappingManagementDialogComponent,
} from '../../components/account-mapping-management-dialog/account-mapping-management-dialog.component';
import { filter, noop, of, switchMap } from 'rxjs';
import { AssetManagementActions } from '../../state/asset-management.actions';
import { Store } from '@ngxs/store';
import { TsSnackbarService } from 'tsui';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { AssetManagementState } from '../../state/asset-management.state';
import { AsyncPipe } from '@angular/common';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { combineLatestWith, finalize, take, tap } from 'rxjs/operators';
import { AccountMapping } from '../../models/account-mapping';
import { AssetManagementMessages } from '../../utils/asset-management-messages.utils';

@Component({
  selector: 'account-mapping-container',
  standalone: true,
  imports: [AccountMappingGridComponent, AsyncPipe],
  templateUrl: './account-mapping-container.component.html',
  styleUrl: './account-mapping-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountMappingContainerComponent implements OnInit {
  readonly dialog = inject(Dialog);
  readonly store = inject(Store);
  readonly confirmationDialogService = inject(ConfirmationDialogService);

  readonly snackbar = inject(TsSnackbarService);

  readonly accountMapping$ = this.store.select(AssetManagementState.accountMapping);
  readonly unmappedAccounts$ = this.store.select(AssetManagementState.unmappedAccounts);
  readonly loading = signal(true);

  get selectedAccountTreeId() {
    // TODO: improve this once we allow the selection and creation of multiple account trees
    return this.store.selectSnapshot(AssetManagementState.accountTree).results[0]?.id;
  }

  ngOnInit(): void {
    const loadAccountTree$ = this.store.selectSnapshot(AssetManagementState.accountTree)
      ? of(true)
      : this.store.dispatch(new AssetManagementActions.LoadAccountTrees());

    loadAccountTree$
      .pipe(
        switchMap(() => {
          return this.store
            .dispatch(
              new AssetManagementActions.LoadAccountMappings(this.selectedAccountTreeId, {
                page: 1,
                per_page: 100,
                q: '',
              }),
            )
            .pipe(combineLatestWith(this.store.dispatch(new AssetManagementActions.LoadRegularAccounts())));
        }),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          AssetManagementMessages.Success.LOAD('Account Mappings'),
          AssetManagementMessages.Error.LOAD('Account Mappings'),
        ),
        finalize(() => this.loading.set(false)),
        take(1),
      )
      .subscribe(noop);
  }

  openAccountMappingImportDialog() {
    const dialogRef = this.dialog.open(AccountMappingImportDialogComponent, {
      ...TsDialogComponent.getDefaultDialogConfig(),
    });

    dialogRef.closed.pipe(filter((result) => !!result)).subscribe(() => {
      this.store.dispatch(
        new AssetManagementActions.LoadAccountMappings(this.selectedAccountTreeId, { page: 1, per_page: 100, q: '' }),
      );
    });
  }

  openAccountMappingManagementDialog({
    account_mapping_id,
    category_account_id,
    regular_account_id,
  }: {
    account_mapping_id?: number;
    category_account_id?: number;
    regular_account_id?: number;
  }) {
    const dialogRef = this.dialog.open(AccountMappingManagementDialogComponent, {
      ...TsDialogComponent.getDefaultDialogConfig(),
      data: {
        account_mapping_id,
        category_account_id,
        regular_account_id,
      },
    });

    dialogRef.closed
      .pipe(
        filter((result) => !!result),
        switchMap((result: Required<AccountMappingDialogData>) => {
          if (!result?.account_mapping_id) {
            return this.store.dispatch(
              new AssetManagementActions.CreateAccountMapping(this.selectedAccountTreeId, result),
            );
          }

          return this.store.dispatch(
            new AssetManagementActions.UpdateAccountMapping(this.selectedAccountTreeId, result),
          );
        }),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          'Account mapping saved successfully',
          'Failed to save account mapping',
        ),
      )
      .subscribe(() => {
        this.store.dispatch(
          new AssetManagementActions.LoadAccountMappings(this.selectedAccountTreeId, { page: 1, per_page: 100, q: '' }),
        );
      });
  }

  deleteAccountMapping(accountMapping: AccountMapping) {
    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: `Delete Mapping: ${accountMapping?.gl_account.gl_account_name} <-> ${accountMapping?.gl_category.name}`,
        ...ConfirmationDialogService.deleteConfirmationCommonLayoutConfig(),
      })
      .pipe(
        filter((result) => result),
        tap(() => this.loading.set(true)),
        switchMap(() => {
          return this.store.dispatch(
            new AssetManagementActions.DeleteAccountMapping(this.selectedAccountTreeId, accountMapping),
          );
        }),
        switchMap(() => {
          return this.store.dispatch(
            new AssetManagementActions.LoadAccountMappings(this.selectedAccountTreeId, {
              page: 1,
              per_page: 100,
              q: '',
            }),
          );
        }),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          'Account mapping deleted successfully',
          'Failed to delete account mapping',
        ),
        finalize(() => this.loading.set(false)),
        take(1),
      )
      .subscribe(noop);
  }
}
